import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

interface DatadogRumConfig {
  applicationId?: string;
  clientToken?: string;
  env: string;
  apiBaseUrl: string;
  client?: string;
}

/*
 * Whether init actually ran. The RUM SDK buffers calls made before init and
 * drops them if it never comes, so in development and in tests every
 * addAction/setUser would queue against an SDK that is never going to flush.
 * The analytics sink reads this and stays a true no-op instead.
 */
let initialised = false;

export function isDatadogRumEnabled(): boolean {
  return initialised;
}

export function initDatadogRum({
  applicationId,
  clientToken,
  env,
  apiBaseUrl,
  client,
}: DatadogRumConfig): void {
  if (env === "development" || !applicationId || !clientToken) return;
  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: "healthos-web",
    env,
    version: __APP_VERSION__,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: apiBaseUrl ? [apiBaseUrl] : [],
    defaultPrivacyLevel: "mask-user-input",
    plugins: [reactPlugin({ router: true })],
    // Embedded in the desktop shell we run as a cross-origin iframe inside a
    // Tauri webview, where the default cookie-backed session cannot be stored
    // and RUM silently drops all data. localStorage persists there, matching
    // the shell's own RUM workaround. Standalone web keeps the cookie default.
    ...(client ? { sessionPersistence: "local-storage" as const } : {}),
  });
  initialised = true;
  if (client) {
    datadogRum.setGlobalContextProperty("client", client);
  }
}

/**
 * Set a global RUM attribute for the session, or remove it when null.
 *
 * Global context lands on every subsequent RUM event as `@context.<key>`, so a
 * value set once here becomes a facet that any action, error, or view can be
 * grouped by. That is what lets one attribute (`days_since_onboarding`) turn
 * every behaviour into a retention curve rather than needing an event each.
 */
export function setDatadogContextProperty(key: string, value: string | number | null): void {
  if (!initialised) return;
  if (value === null) datadogRum.removeGlobalContextProperty(key);
  else datadogRum.setGlobalContextProperty(key, value);
}

/*
 * The RUM user object is owned here, not by callers.
 *
 * `setUser` REPLACES the whole `usr` object (it maps to the context manager's
 * `setContext`, which assigns rather than merges), while `setUserProperty`
 * spreads onto it. So any caller that re-identifies the member silently drops
 * every property set separately. That is not a rare path: a token renew hands
 * AuthProvider a new state object every few minutes, and the effect watching it
 * re-identifies each time, which would leave most of a session untagged.
 *
 * Remembering the company here and re-applying it on every `setUser` makes that
 * unrepresentable: identify as often as you like, the company survives.
 */
let userCompanyId: string | null = null;

/**
 * Identify the member. Id only, never email or name: no PII or PHI in RUM.
 *
 * Always go through this rather than `datadogRum.setUser` so the company tag
 * is reapplied; see the note above.
 */
export function setDatadogUser(id: string): void {
  if (!initialised) return;
  datadogRum.setUser(userCompanyId ? { id, companyId: userCompanyId } : { id });
}

/** Drop the member, and the company along with them, on sign-out. */
export function clearDatadogUser(): void {
  userCompanyId = null;
  if (!initialised) return;
  datadogRum.clearUser();
}

/**
 * Attach the member's covered entity to the RUM user, so every subsequent
 * event carries `usr.companyId`.
 *
 * A user property rather than global context on purpose: it rides views,
 * actions, errors, resources, long tasks and Session Replay alike, it is
 * dropped by `clearUser()` on sign-out along with the id it belongs to, and it
 * lands in the same `usr.*` namespace Datadog's user facets already read. That
 * makes "which customer is seeing this error" and "what does the funnel look
 * like for one account" a group-by rather than a bespoke query.
 *
 * An opaque tenant uuid only, never a company name, matching the id-only rule
 * `setDatadogUser` follows.
 */
export function setDatadogUserCompany(companyId: string | null | undefined): void {
  // Remembered even before init, so a value that arrives while RUM is still
  // booting is not lost: the next setUser picks it up.
  userCompanyId = companyId ?? null;
  if (!initialised || !companyId) return;
  datadogRum.setUserProperty("companyId", companyId);
}

/**
 * Record a feature flag the member was exposed to, for Datadog's Feature Flag
 * Tracking.
 *
 * This is what puts a flag on the RUM Feature Flags page and lets an error or a
 * slow view be filtered by who had the flag on. Datadog has no server-side
 * equivalent, so a flag only appears here if the browser learns its value;
 * HealthOS evaluates every flag in core-api, which is why these calls sit where
 * an already-fetched value is read rather than next to a ConfigCat call.
 *
 * Report both outcomes. A flag reported only when true cannot answer "is this
 * error specific to members who have it", because the control group is missing.
 *
 * Pass the key from the `FLAGS` manifest rather than a literal, so the key
 * cannot drift from ConfigCat and the code-references scan counts this as a
 * usage.
 */
export function reportDatadogFeatureFlag(key: string, value: boolean | string | number): void {
  if (!initialised) return;
  datadogRum.addFeatureFlagEvaluation(key, value);
}

/**
 * Tag the RUM session with where in a multi-step flow the member is.
 *
 * Funnel steps themselves are custom actions, not views: the whole wizard lives
 * at one route, and minting a synthetic view per step would double-count views
 * and skew the Core Web Vitals the existing dashboards read. This instead
 * stamps the step onto the session, so an error, a long task, or a Session
 * Replay recorded during onboarding says which step it happened on.
 *
 * Pass null on leaving the flow so a later error is not misattributed to it.
 */
export function setDatadogFlowStep(flow: string, step: string | null): void {
  setDatadogContextProperty(flow, step);
}
